"use client";

import { type RefObject, useEffect } from "react";
import { isNativePlatform } from "@/lib/native/native-splash-gate";

/** Degrees of tilt away from how the phone is being held that reach full effect. */
const FULL_TILT_DEG = 18;
/**
 * How quickly "how the phone is being held" catches up with the phone, in
 * seconds. The light answers movement, not absolute angle, so a phone held
 * flat on a table and one held upright both rest on the same lighting.
 */
const REST_FOLLOW_S = 1.6;

type PermissionedOrientationEvent = typeof DeviceOrientationEvent & {
  requestPermission?: () => Promise<"granted" | "denied" | "default">;
};

/** A real reading has arrived, so nothing needs asking for. */
let sensorLive = false;
// Asked once per page life: WebKit remembers a refusal for the session and
// a second prompt would only be rejected.
let asked = false;

/**
 * iOS delivers orientation only after `requestPermission()`, which it only
 * allows from a user activation (a click, not a touch `pointerdown`). Callers
 * invoke this from the tap that already means "I am handling the card".
 *
 * Chrome exposes the same method but streams readings without it, so the
 * test is whether readings are arriving, not whether the method exists —
 * an Android phone is never prompted.
 *
 * In the iOS app there is no prompt at all (Capacitor's WKUIDelegate grants
 * motion access), but WebKit still sends nothing until this is called from a
 * gesture, so `useDeviceTilt` also calls it on the first touch anywhere.
 */
export function requestDeviceTiltAccess() {
  if (sensorLive || asked || typeof window === "undefined") return;
  const Event = window.DeviceOrientationEvent as
    | PermissionedOrientationEvent
    | undefined;
  if (typeof Event?.requestPermission !== "function") return;
  asked = true;
  // A grant starts events on the listener that is already attached.
  Event.requestPermission().catch(() => {});
}

const clampUnit = (value: number) => Math.max(-1, Math.min(1, value));

/**
 * Reports how far the device has moved from the way it is being held, as
 * -1..1 on each axis (x: rolled right, y: pitched towards the viewer).
 *
 * Listens only while `target` is on screen and the page is visible, so a
 * wallet parked behind another route never runs a sensor handler.
 */
export function useDeviceTilt(
  target: RefObject<Element | null>,
  enabled: boolean,
  onTilt: (x: number, y: number) => void,
) {
  useEffect(() => {
    const element = target.current;
    if (!enabled || !element || !("DeviceOrientationEvent" in window)) return;

    let listening = false;
    let visible = false;
    let rest: { pitch: number; roll: number } | null = null;
    let lastAt = 0;

    const handle = (event: DeviceOrientationEvent) => {
      // Desktop Chrome fires one event with nulls and nothing after.
      if (event.beta === null || event.gamma === null) return;
      sensorLive = true;
      // Screen axes, not device axes: in landscape the roll is the beta.
      const angle = screen.orientation?.angle ?? 0;
      const sideways = angle === 90 || angle === 270;
      const flip = angle === 90 ? 1 : -1;
      const roll = sideways ? event.beta * flip : event.gamma;
      const pitch = sideways ? -event.gamma * flip : event.beta;

      const now = performance.now();
      if (!rest) rest = { pitch, roll };
      const follow = 1 - Math.exp(-(now - lastAt) / 1000 / REST_FOLLOW_S);
      lastAt = now;
      rest.pitch += (pitch - rest.pitch) * follow;
      rest.roll += (roll - rest.roll) * follow;

      onTilt(
        clampUnit((roll - rest.roll) / FULL_TILT_DEG),
        clampUnit((pitch - rest.pitch) / FULL_TILT_DEG),
      );
    };

    const sync = () => {
      const want = visible && !document.hidden;
      if (want === listening) return;
      listening = want;
      if (want) {
        rest = null;
        lastAt = performance.now();
        window.addEventListener("deviceorientation", handle);
      } else {
        window.removeEventListener("deviceorientation", handle);
        onTilt(0, 0);
      }
    };

    const observer = new IntersectionObserver(([entry]) => {
      visible = Boolean(entry?.isIntersecting);
      sync();
    });
    observer.observe(element);
    document.addEventListener("visibilitychange", sync);

    // Native iOS: the grant is silent, so any first touch may ask for it —
    // not only a tap on the card, which in Safari shows a system prompt.
    const unlockOnTouch = () => {
      removeUnlock();
      requestDeviceTiltAccess();
    };
    const removeUnlock = () => {
      window.removeEventListener("touchend", unlockOnTouch, true);
      window.removeEventListener("click", unlockOnTouch, true);
    };
    if (isNativePlatform() && !sensorLive && !asked) {
      window.addEventListener("touchend", unlockOnTouch, true);
      window.addEventListener("click", unlockOnTouch, true);
    }

    return () => {
      removeUnlock();
      observer.disconnect();
      document.removeEventListener("visibilitychange", sync);
      window.removeEventListener("deviceorientation", handle);
    };
  }, [enabled, onTilt, target]);
}
